<template>
    <div>
        <city-tabs
            v-model="currentCityId"
            :city-list="cityList"
        />
        <el-button
            v-if="cityList && cityList.length > 1"
            type="primary"
            @click="handleCopyCityListVisible"
            style="margin-top: 12px;"
        >
            一键复制到其他城市
        </el-button>
        <city-edit
            v-for="(cityInfo, cityId) in allCityStateInfo"
            v-show="cityId == currentCityId"
            :ref="`city-${cityId}`"
            :key="cityId"
            :mode="mode"
            :current-city-info="cityInfo"
            :current-city-id="+cityId"
            :create-info-model="createInfoModel"
            :base-info-model="baseInfoModel"
            :is-supplies="isSupplies"
            :is-discount-sku="isDiscountSku"
            :focus-attr-data="focusAttrData"
            :pre-sku-id="preSkuId"
            :sku-base-attributes="skuBaseAttributes"
            :is-create-new-apply-page="createProductType"
        />
        <copy-city-list
            ref="copyCityListRef"
            :city-list="cityList"
            :current-city-id="currentCityId"
            :all-area-city-list="allAreaCityList"
            @success="handleCopyCityListSuccess"
        />
    </div>
</template>

<script>
import { parseQuery } from '@mcfe/tools';
import emitter from 'utils/emitter';
import { getCategoryInfoById } from 'utils/category';
import { cloneDeep } from '@lsfe/tools';
import { reportMetric } from 'utils/reportMetric';
import CityEdit from './edit';
import CityTabs from '../components/CityTabs';
import {
    createNewToken, citySalesInfoToken, shared,
} from '../shareState';
import { getBaseModel } from '../baseModel';
import CopyCityList from './CopyCityList';
import { CREATE_NEW_PRODUCT_STEP_NAMES, CREATE_NEW_PRODUCT_STEP } from '../config';


const baseModel = getBaseModel();
const initState = () => ({
    manufacturerId: '', // 生产商
    manufacturerName: '', // 生产商名
    purchasePrice: undefined, // 城市采购价
    shippingType: '', // 配送方式
    rdcIdList: [], // 仓
    thawingRdcIdList: [], // 解冻仓列表
    sellPrice: undefined, // 城市售价
    citySkuAttributes: [], // 扩展属性，解决复制不过来
});

export default {
    name: citySalesInfoToken,
    components: {
        CityEdit,
        CopyCityList,
        CityTabs,
    },
    mixins: [emitter],
    props: {
        createProductType: {
            type: String,
            default: '新建品',
        },
    },
    data() {
        return {
            cityList: [],
            currentCityId: '',
            searchCityId: '', // 搜索城市 ID
            allCityStateInfo: {}, // 城市数据模型
            // showUnitList: [], // 购买步长单位列表
            // originUnitList: [], // 单位列表
            createInfoModel: {}, // 提报信息数据
            baseInfoModel: {}, // 基础信息数据
            mode: 'edit',
            haveInit: false, // 暂时没有用到
            existedCityIds: shared.existedCityIds,
            focusAttrData: {}, // c端展示重点属性
            skuBaseAttributes: [], // 商品基础属性
            batchToCityIds: [], // 一键复制到其他城市
        };
    },
    computed: {
        currentStateInfo() {
            return this.allCityStateInfo[+this.currentCityId];
        },
        // 区域城市列表，用于 BasicPlanCitySelect
        allAreaCityList() {
            return shared.allAreaCityList;
        },
        // 分类品类是物资品
        isSupplies() {
            const target = getCategoryInfoById(this.categoryId, shared.categoryTreeData);
            return target && target.categoryType === 2;
        },
        categoryId() {
            return this.createInfoModel.categoryId;
        },
        // 是否是b货  货品类型 = 特惠 为b品
        isDiscountSku() {
            // attrId === 20019 为货品类型
            const productType = this.createInfoModel.keyAttributes.find((item) => item.attrId === 20019);
            // id === 56365 为特惠
            return productType && productType.selectedValues[0].id === 56365;
        },
        preSkuId() {
            return parseQuery().encryptedPreSkuId || '';
        },
        createType() {
            return getBaseModel().models.createType;
        },
        // 获取用户实际选中的PB档案城市列表（格式：cityId）
        selectedPdArchiveCities() {
            if (!this.preSkuId) {
                return [];
            }
            // 获取 PB 档案城市列表（与 baozhiqi.vue 逻辑保持一致）
            const cityPreSkuBriefInfoList = baseModel.models.cityPreSkuBriefInfoList || [];
            let pdArchiveCityIds = [];
            if (cityPreSkuBriefInfoList.length > 0) {
                pdArchiveCityIds = cityPreSkuBriefInfoList.map((item) => item.cityId);
            } else {
                // 如果没有 cityPreSkuBriefInfoList，从 citySkuManageSection 获取
                const citySkuManageSection = baseModel.models.citySkuManageSection || baseModel.data?.citySkuManageSection;
                const citySkuManageInfo = citySkuManageSection?.citySkuManageInfo || [];
                pdArchiveCityIds = citySkuManageInfo.map((item) => item.cityId);
            }
            if (!pdArchiveCityIds.length) {
                return [];
            }
            // 从当前选中的城市中过滤出 PB 档案城市
            const currentSelectedCityIds = this.createInfoModel.cityIds || [];
            return currentSelectedCityIds.filter((cityId) => pdArchiveCityIds.includes(cityId));
        },
        // PB档案城市的供应商和生产商信息
        pdArchiveSupplierAndManufacturerInfo() {
            // 默认返回值
            const defaultResult = {
                isUniqueManufacturer: true,
                manufacturerOptions: [],
            };
            if (!this.preSkuId) {
                return defaultResult;
            }
            // 获取PB档案城市列表
            const citySkuManageSection = cloneDeep(baseModel.data?.citySkuManageSection) || {};
            const citySkuManageInfo = cloneDeep(citySkuManageSection?.citySkuManageInfo) || [];
            if (!citySkuManageInfo.length) {
                return defaultResult;
            }
            // 筛选出用户实际选中的PB档案城市
            let selectedPdArchiveCityInfos = [];
            if (this.selectedPdArchiveCities.length > 0) {
                const selectedPdArchiveCityIdSet = new Set(this.selectedPdArchiveCities);
                selectedPdArchiveCityInfos = citySkuManageInfo.filter((cityInfo) => {
                    const cityId = cityInfo.cityId;
                    return selectedPdArchiveCityIdSet.has(cityId);
                });
            }
            // 如果没有选中的档案城市，使用所有档案城市
            if (selectedPdArchiveCityInfos.length === 0) {
                selectedPdArchiveCityInfos = citySkuManageInfo;
            }
            // 收集生产商信息
            const manufacturerIds = new Set();
            const manufacturerOptionsMap = new Map(); // manufacturerId -> {id, name}
            selectedPdArchiveCityInfos.forEach((cityInfo) => {
                if (cityInfo.manufacturerId) {
                    manufacturerIds.add(cityInfo.manufacturerId);
                    if (!manufacturerOptionsMap.has(cityInfo.manufacturerId)) {
                        manufacturerOptionsMap.set(cityInfo.manufacturerId, {
                            id: cityInfo.manufacturerId,
                            name: cityInfo.manufacturerName || '',
                        });
                    }
                }
            });
            // 如果生产商唯一，自动填充新增城市的生产商信息
            const isUniqueManufacturer = manufacturerIds.size <= 1;
            if (isUniqueManufacturer && manufacturerIds.size === 1) {
                // 获取PB档案城市的生产商信息
                let pdArchiveCityInfo = null;
                if (this.selectedPdArchiveCities.length > 0) {
                    // 优先从选中的档案城市中获取
                    const selectedPdArchiveCityIdSet = new Set(this.selectedPdArchiveCities);
                    const filteredCityInfos = citySkuManageInfo.filter((cityInfo) => {
                        const cityId = cityInfo.cityId;
                        return selectedPdArchiveCityIdSet.has(cityId);
                    });
                    pdArchiveCityInfo = filteredCityInfos.find(
                        (info) => info.manufacturerId,
                    );
                }
                // 如果从选中的档案城市中找不到，使用所有档案城市（兜底）
                if (!pdArchiveCityInfo) {
                    pdArchiveCityInfo = citySkuManageInfo.find(
                        (info) => info.manufacturerId,
                    );
                }
                // 如果找到了生产商信息，将新增城市的生产商信息赋值为PB档案城市的生产商信息
                if (pdArchiveCityInfo) {
                    const { manufacturerId, manufacturerName } = pdArchiveCityInfo;
                    // 构建 pb 档案城市的 ID 映射（所有档案城市，用于判断是否是新增城市）
                    const pdArchiveCityIdMap = new Set(
                        citySkuManageInfo.map((item) => String(item.cityId)),
                    );
                    // 将生产商信息复制到新增城市（不在 pb 档案中的城市）
                    Object.values(this.allCityStateInfo).forEach((cityInfo) => {
                        // 如果是新增城市（不在 pb 档案中），则复制manufacturerId
                        if (!pdArchiveCityIdMap.has(String(cityInfo.cityId))) {
                            if (manufacturerId && !cityInfo.manufacturerId) {
                                cityInfo.manufacturerId = manufacturerId;
                            }
                            if (manufacturerName && !cityInfo.manufacturerName) {
                                cityInfo.manufacturerName = manufacturerName;
                            }
                        }
                    });
                }
            }
            const manufacturerOptions = Array.from(manufacturerOptionsMap.values());
            return {
                isUniqueManufacturer,
                manufacturerOptions,
            };
        },
    },
    created() {
        baseModel.registerVM(this, citySalesInfoToken);
    },
    async activated() {
        if (shared.stepChangeType === 'next') {
            this.handlePagesWhetherPages();
            try {
                await this.init();
            } catch (error) {
                console.warn(error);
            } finally {
                this.dispatch(createNewToken, 'page-loaded', true);
            }
        }
    },
    methods: {
        handlePagesWhetherPages() {
            reportMetric('supplier_createNewProduct_current_step_whether_render_or_not', 1, {
                createNewProductStepName: CREATE_NEW_PRODUCT_STEP_NAMES[CREATE_NEW_PRODUCT_STEP.CITY_SALES_INFO],
                createProductType: this.createProductType,
                presetSkuId: baseModel.models.presetSkuId,
                currentStep: CREATE_NEW_PRODUCT_STEP.CITY_SALES_INFO,
            });
        },
        // 搜索城市并切换到对应的tab
        handleCitySearch(selectedCityId) {
            if (selectedCityId) {
                this.currentCityId = selectedCityId;
            }
        },
        // 打开一键复制到其他城市弹窗
        handleCopyCityListVisible() {
            if (this.$refs.copyCityListRef) {
                this.$refs.copyCityListRef.popup();
            }
        },
        // 一键复制到其他城市成功
        handleCopyCityListSuccess(toCityIds) {
            if (!toCityIds || !toCityIds.length) {
                this.$message.warning('未选择目标城市，复制操作已取消');
            }

            // 获取当前城市信息的深拷贝
            const currentCityInfo = cloneDeep(this.allCityStateInfo[this.currentCityId]);

            // 遍历目标城市ID数组，只复制到选中的城市
            toCityIds.forEach((cityId) => {
                const item = this.allCityStateInfo[cityId];
                if (!item) return;

                // 检查配送方式是否发生变化
                const isShippingTypeChanged = item.shippingType !== currentCityInfo.shippingType;

                // 不复制字段列表
                const exclude = ['cityId', 'rdcIdList', 'thawingRdcIdList'];

                // 如果当前城市有citySkuAttributes但目标城市没有，需要先添加该属性
                if (currentCityInfo.citySkuAttributes && !item.hasOwnProperty('citySkuAttributes')) {
                    this.$set(item, 'citySkuAttributes', []);
                }

                Object.keys(item).forEach((prop) => {
                    // if (!exclude.includes(prop)) {
                    //     item[prop] = currentCityInfo[prop];
                    // }
                    if (!exclude.includes(prop)) {
                        if (prop === 'citySkuAttributes') {
                            // 1. 先创建一个深拷贝的新数组
                            const newAttributes = cloneDeep(currentCityInfo.citySkuAttributes);


                            // 2. 使用nextTick确保DOM更新后再设置新值
                            this.$nextTick(() => {
                                // 3. 使用Vue.set确保响应式更新
                                this.$set(item, 'citySkuAttributes', newAttributes);

                                // 4. 通知子组件更新
                                this.$nextTick(() => {
                                    const cityVm = this.$refs[`city-${item.cityId}`];
                                    if (cityVm && cityVm[0] && typeof cityVm[0].refreshAttributes === 'function') {
                                        cityVm[0].refreshAttributes();
                                    }
                                });
                            });
                        } else {
                            item[prop] = currentCityInfo[prop];
                        }
                    }
                });

                // 如果配送方式发生变化，清空rdcIdList和thawingRdcIdList
                if (isShippingTypeChanged) {
                    item.rdcIdList = [];
                    item.thawingRdcIdList = [];
                }
            });
            this.$message.success('复制成功');
        },
        async init() {
            try {
                const draft = await this.fetchNewCreateData(baseModel.models.applyId);
                this.skuBaseAttributes = draft?.skuBaseSection?.skuBaseAttributes || [];
                baseModel.setData(draft);
                const citySalesInfo = baseModel.initCitySalesInfo;
                this.createInfoModel = baseModel.initCreateInfo.form;
                this.baseInfoModel = baseModel.initProductBaseInfo;
                this.cityList = this.getSortedCityList();
                this.allCityStateInfo = this.formatCityStateInfoWithDetail(this.cityList, citySalesInfo);

                // 初始化 currentCityId
                this.currentCityId = this.cityList[0].value;
                // 将 pb 档案城市的 supplierId 和 manufacturerId 复制到新增城市
                this.copyPbArchiveSupplierAndManufacturer();
                this.$nextTick(() => {
                    this.cityList.forEach((city) => {
                        const cityVm = this.$refs[`city-${city.value}`];
                        if (cityVm && cityVm[0]) {
                            cityVm[0].init();
                        }
                    });
                    this.$nextTick(() => {
                        reportMetric('supplier_create_newProduct_pages_render_success', 1, {
                            createNewProductStepName: CREATE_NEW_PRODUCT_STEP_NAMES[CREATE_NEW_PRODUCT_STEP.CITY_SALES_INFO],
                            createProductType: this.createProductType,
                            presetSkuId: baseModel.models.presetSkuId,
                            currentStep: CREATE_NEW_PRODUCT_STEP.CITY_SALES_INFO,
                            value: 1,
                        });
                    });
                });
            } catch (error) {
                reportMetric('supplier_create_newProduct_pages_render_success', 0, {
                    createNewProductStepName: CREATE_NEW_PRODUCT_STEP_NAMES[CREATE_NEW_PRODUCT_STEP.CITY_SALES_INFO],
                    createProductType: this.createProductType,
                    presetSkuId: baseModel.models.presetSkuId,
                    currentStep: CREATE_NEW_PRODUCT_STEP.CITY_SALES_INFO,
                    value: 0,
                });
                this.$message.error(error);
            }

            this.queryFocusAttr();
        },

        // 获取重点属性
        async queryFocusAttr() {
            if (!this.createInfoModel.bgCategoryId) return;
            const res = await this.$hPost('/api/vss/shepherd/ProductAttrDescQueryTService/queryAttrDescByBgCategoryId', { bgCategoryId: this.createInfoModel?.bgCategoryId || this.form?.bgCategoryId }).catch(() => ({}));

            this.focusAttrData = {
                extAttrList: (res.extAttrDesc || []).filter((item) => item.appDisplay).map((item) => item.attrId),
            };
            (res.fixedAttrDesc || []).forEach((item) => {
                this.focusAttrData[item.code] = item.appDisplay;
            });
        },

        // 将 pb 档案城市的 manufacturerId 复制到新增城市
        copyPbArchiveSupplierAndManufacturer() {
            if (!this.preSkuId) {
                return;
            }
            const pdArchiveInfo = this.pdArchiveSupplierAndManufacturerInfo;
            // 如果生产商都唯一，才自动填充
            if (!pdArchiveInfo.isUniqueManufacturer) {
                // 如果不唯一，不自动填充，让用户在 edit.vue 中使用 mc-select 选择
                return;
            }
            // 如果没有生产商选项，无法填充
            if (pdArchiveInfo.manufacturerOptions.length === 0) {
                return;
            }
            // 获取 pb 档案城市列表
            const citySkuManageSection = baseModel.data?.citySkuManageSection;
            const citySkuManageInfo = citySkuManageSection?.citySkuManageInfo || [];
            // 构建 pb 档案城市的 ID 映射（所有档案城市，用于判断是否是新增城市）
            const pdArchiveCityIdMap = new Set(
                citySkuManageInfo.map((item) => item.cityId),
            );
            if (pdArchiveCityIdMap.size === 0) {
                return;
            }
            // 从 pdArchiveInfo 中获取供应商和生产商信息（优先使用选中的档案城市，如果没有则使用所有档案城市）
            let pdArchiveCityInfo = null;
            if (this.selectedPdArchiveCities.length > 0) {
                // 优先从选中的档案城市中获取
                const selectedPdArchiveCityIdSet = new Set(this.selectedPdArchiveCities);
                const selectedPdArchiveCityInfos = citySkuManageInfo.filter((cityInfo) => {
                    const cityId = cityInfo.cityId;
                    return selectedPdArchiveCityIdSet.has(cityId);
                });
                pdArchiveCityInfo = selectedPdArchiveCityInfos.find(
                    (info) => info.manufacturerId,
                );
            }
            // 如果从选中的档案城市中找不到，使用所有档案城市（兜底）
            if (!pdArchiveCityInfo) {
                pdArchiveCityInfo = citySkuManageInfo.find(
                    (info) => info.manufacturerId,
                );
            }
            if (!pdArchiveCityInfo) {
                return;
            }
            const {
                manufacturerId, manufacturerName,
            } = pdArchiveCityInfo;
            // 将 supplierId 和 manufacturerId 复制到新增城市（不在 pb 档案中的城市）
            Object.values(this.allCityStateInfo).forEach((cityInfo) => {
                // 如果是新增城市（不在 pb 档案中），则复制manufacturerId
                if (!pdArchiveCityIdMap.has(String(cityInfo.cityId))) {
                    if (manufacturerId && !cityInfo.manufacturerId) {
                        cityInfo.manufacturerId = manufacturerId;
                    }
                    if (manufacturerName && !cityInfo.manufacturerName) {
                        cityInfo.manufacturerName = manufacturerName;
                    }
                }
            });
        },

        formatCityStateInfoWithDetail(cityList, citySalesInfo) {
            const newStateInfo = (citySalesInfo || []).reduce((result, item) => {
                result[item.cityId] = {
                    cityId: item.cityId,
                    manufacturerId: item.manufacturerId || '',
                    manufacturerName: item.manufacturerName || '',
                    shippingType: item.shippingType || '',
                    rdcIdList: item.rdcIdList || [],
                    thawingRdcIdList: item.thawingRdcIdList || [],
                    purchasePrice: item.purchasePrice || undefined, // 城市采购价
                    sellPrice: item.sellPrice || undefined, // 售价
                    citySkuAttributes: item.citySkuAttributes,
                };
                return result;
            }, {});
            const allCityStateInfo = cityList.reduce((result, item) => {
                result[item.value] = newStateInfo[item.value] ? {
                    cityId: item.value,
                    ...newStateInfo[item.value],
                } : {
                    cityId: item.value,
                    ...initState(),
                };
                return result;
            }, {});
            return allCityStateInfo;
        },
        async fetchNewCreateData(applyId) {
            const draftData = await this.$service.fetchNewSellDetail({ applyId });
            return draftData;
        },
        /**
         * 获取排序后的城市列表
         * 先按照区域ID升序排序，再按照城市ID升序排序展示城市
         */
        getSortedCityList() {
            const selectedCityIds = this.createInfoModel.cityIds || [];
            const sortedCityList = [];

            // 从 allAreaCityList 中获取，先按照区域ID升序排序
            const allAreaCityList = this.allAreaCityList || [];

            // 兜底逻辑：检查数据结构
            if (!allAreaCityList || allAreaCityList.length === 0) {
                return sortedCityList;
            }

            // 获取 areaConfigList，兜底处理
            const areaConfigList = allAreaCityList[0]?.areaConfigList || [];
            if (!areaConfigList || areaConfigList.length === 0) {
                return sortedCityList;
            }

            // 对 areaConfigList 按照区域ID升序排序
            const sortedAreaConfigs = areaConfigList.sort((a, b) => {
                return (a.areaId || 0) - (b.areaId || 0);
            });

            sortedAreaConfigs.forEach((areaConfig) => {
                const manageCityList = areaConfig.manageCityList || [];

                // 兜底逻辑：检查 manageCityList 是否存在
                if (!manageCityList || manageCityList.length === 0) {
                    return; // 跳过此区域
                }

                // 在该区域下的城市中，按城市ID升序排序
                const sortedManageCities = manageCityList.sort((a, b) => {
                    return (a.manageCityId || 0) - (b.manageCityId || 0);
                });

                sortedManageCities.forEach((city) => {
                    const cityId = city.manageCityId;

                    // 兜底逻辑：检查 cityId 是否有效
                    if (!cityId) {
                        return; // 跳过无效的城市
                    }

                    // 检查该城市是否在选中的城市列表中
                    if (selectedCityIds.includes(cityId)) {
                        sortedCityList.push({
                            value: cityId,
                            label: city.manageCityName || '',
                        });
                    }
                });
            });

            return sortedCityList;
        },
        getVMPostData() {
            return this.allCityStateInfo;
        },
        async validate() {
            let validResult = true;
            // 校验所有城市
            const errors = [];
            for (let i = 0; i < this.cityList.length; i++) {
                const item = this.cityList[i];
                const valid = await this.$refs[`city-${item.value}`][0].validate();
                if (!valid) errors.push(item.label);
            }
            if (errors.length) {
                this.$message.error(`【${errors.join(',')}】填写信息有误`);
                validResult = false;
            }
            return validResult;
        },
    },
};
</script>
